import { useIntl } from 'react-intl';
import { Carousel, CarouselPage } from '../carousel/carousel';
import { OnboardingContent } from './onboarding-content';
import { messages } from './onboarding-messages';
import { Flag, useFeatureFlag, useLanguage } from '@algoan/dashboard/utils';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import {
  showOnboardingSelector,
  skipOnboardingSelector,
  isRecategorizationEnabledSelector,
  userRoleSelector,
} from '@algoan/dashboard/store/selectors';
import { closeOnboarding, toggleSkipOnboarding } from '@algoan/dashboard/store/slice';
import { useEffect, useMemo, useState } from 'react';
import { useModal } from '../global-modal/use-modal';
import { useIsFirstConnectionAfterDashboardRegistration } from '../../hooks/use-is-first-connection-after-dashboard-registration';

export const Onboarding: React.FC = () => {
  const intl = useIntl();
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const { Modal } = useModal();
  const showOnboarding = useSelector(showOnboardingSelector);
  const skipOnboarding = useSelector(skipOnboardingSelector);
  const isRecategorizationEnabled = useSelector(isRecategorizationEnabledSelector);
  const [hideSkipMessage, setHideSkipMessage] = useState(skipOnboarding);
  const { isInitiator } = useSelector(userRoleSelector);
  const isFirstConnectionAfterDashboardRegistration = useIsFirstConnectionAfterDashboardRegistration();
  const { active: isExportButtonHidden } = useFeatureFlag(Flag.HIDE_EXPORT_BUTTON);
  const { active: hideTheTutorialIsActivated } = useFeatureFlag(Flag.HIDE_THE_TUTORIAL);

  /**
   * If the user skips the onboarding and close the modal, then he
   * won't see the skip message anymore.
   */
  useEffect(() => {
    if (skipOnboarding) {
      setHideSkipMessage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnboarding]);

  const pagesToDisplay = useMemo(() => {
    const pages: CarouselPage[] = [];

    if (!isExportButtonHidden) {
      pages.push({
        title: intl.formatMessage(messages.titleExport),
        content: (
          <OnboardingContent
            video={`/videos/export_pdf_${language}.mp4`}
            description={intl.formatMessage(messages.export)}
          />
        ),
      });
    }

    if (isRecategorizationEnabled) {
      pages.push({
        title: intl.formatMessage(messages.titleModificationsTransactionCategories),
        content: (
          <OnboardingContent
            video={`/videos/recatego_${language}.mp4`}
            description={intl.formatMessage(messages.modificationsTransactionCategories)}
          />
        ),
      });
    }

    pages.push(
      ...[
        {
          title: intl.formatMessage(messages.titleFilterAccounts),
          content: (
            <OnboardingContent
              video={`/videos/filtre_de_compte_${language}.mp4`}
              description={intl.formatMessage(messages.filterAccounts)}
            />
          ),
        },
        {
          title: intl.formatMessage(messages.titleFilterDates),
          content: (
            <OnboardingContent
              video={`/videos/historique_bancaire_${language}.mp4`}
              description={intl.formatMessage(messages.filterDates)}
            />
          ),
        },
        {
          title: intl.formatMessage(messages.titleFilterByCategories),
          content: (
            <OnboardingContent
              video={`/videos/onglet_filtre_${language}.mp4`}
              description={intl.formatMessage(messages.filterByCategories)}
            />
          ),
        },
        {
          title: intl.formatMessage(messages.titleTransactionSearch),
          content: (
            <OnboardingContent
              video={`/videos/recherche_${language}.mp4`}
              description={intl.formatMessage(messages.transactionSearch)}
            />
          ),
        },
        {
          title: intl.formatMessage(messages.titleAddNotes),
          content: (
            <OnboardingContent
              video={`/videos/note_${language}.mp4`}
              description={intl.formatMessage(messages.addNotes)}
            />
          ),
        },
      ],
    );

    return pages;
  }, [intl, isExportButtonHidden, isRecategorizationEnabled, language]);

  if (isInitiator || isFirstConnectionAfterDashboardRegistration || hideTheTutorialIsActivated) {
    return null;
  }

  const closeModal = (): void => {
    dispatch(closeOnboarding());
  };

  return (
    <Modal id="onboarding" open={showOnboarding} onClose={closeModal}>
      <Carousel
        pages={pagesToDisplay}
        isSkipChecked={skipOnboarding}
        onCheckBoxClick={() => dispatch(toggleSkipOnboarding())}
        hideSkipMessage={hideSkipMessage}
      />
    </Modal>
  );
};
